import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	HostListener,
	Input,
	signal,
	viewChild,
} from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { MoveToRootDirective } from '~ng-common-lib/cdk/move-to-root.directive';

import { BaseMenu } from '../../types/menu';

export type WidthSize = 'DEFAULT' | 'FILTER' | 'SMALL' | 'PX80' | 'AUTO' | 'PX240';

export type MenuFloatingPosition = 'RIGHT' | 'TOP'; // | 'BOTTOM' | 'LEFT' ; To be implemented in the future

export const widthSizeMap: Record<WidthSize, number | string> = {
	DEFAULT: 360,
	FILTER: 320,
	PX240: 240,
	SMALL: 216,
	PX80: 80,
	AUTO: 'auto-fit',
};

@Component({
	selector: 'client-floating-menu',
	templateUrl: './floating.component.html',
	styleUrls: ['./floating.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CommonModule, MoveToRootDirective],
})
export class ClientFloatingMenuComponent extends BaseMenu {
	public readonly $wrapperRef = viewChild<ElementRef<HTMLElement>>('wrapper');

	@Input('anchor')
	public set anchor(anchor: HTMLElement | undefined) {
		this.anchor$$.next(anchor);
	}

	public get anchor(): HTMLElement | undefined {
		return this.anchor$$.value;
	}

	@Input('widthSize')
	public widthSize: WidthSize = 'DEFAULT';

	@Input('lightBg')
	public lightBg = true;

	@Input('maxHeight')
	public maxHeight: string | null = null;

	@Input('stickyFooter')
	public stickyFooter = false;

	@Input('hasPaddingTop')
	public hasPaddingTop = false;

	@Input('fullHeight')
	public fullHeight = false;

	@Input('hasBackdrop')
	public hasBackdrop = true;

	@Input('forcePosition')
	public get forcePosition(): MenuFloatingPosition | null {
		return this.$forcePosition();
	}

	public set forcePosition(value: MenuFloatingPosition | null) {
		this.$forcePosition.set(value);
	}

	public readonly $forcePosition = signal<MenuFloatingPosition | null>(null);

	public readonly $positionClasses = computed(() => {
		const position = this.$forcePosition();
		return {
			TOP: position === 'TOP',
			// 'BOTTOM': position === 'BOTTOM',
			// 'LEFT': position === 'LEFT',
			RIGHT: position === 'RIGHT',
		};
	});

	public readonly widthSizeMap = widthSizeMap;

	private _animationFrameId: number | null = null;

	private readonly anchor$$ = new BehaviorSubject<HTMLElement | undefined>(undefined);
	private readonly anchor$ = this.anchor$$.asObservable();

	private readonly visible$$ = new BehaviorSubject(false);
	private readonly visible$ = this.visible$$.asObservable();

	private readonly resized$$ = new BehaviorSubject<void>(undefined);
	private readonly resized$ = this.resized$$.asObservable();

	public readonly box$ = this.visible$.pipe(
		switchMap((visible) => {
			if (!visible) {
				return of(null);
			}

			return this.anchor$.pipe(
				switchMap((anchor) => {
					return this.resized$.pipe(
						map(() => {
							if (!anchor) {
								return null;
							}

							const {
								top: anchorTop,
								left: anchorLeft,
								width: anchorWidth,
								height: anchorHeight,
							} = anchor.getBoundingClientRect();

							const anchorRight = anchorLeft + anchorWidth;
							const anchorBottom = anchorTop + anchorHeight;

							const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

							const availableLeft = anchorRight;
							const availableRight = windowWidth - anchorRight;
							const availableTop = anchorTop;
							const availableBottom = windowHeight - anchorBottom;

							const forcePosition = this.$forcePosition();
							const addAtTop =
								forcePosition === 'TOP' ? true : availableTop > availableBottom;
							const addAtLeft =
								forcePosition === 'TOP' ? true : availableLeft > availableRight;

							return {
								top: anchorTop,
								bottom: windowHeight - anchorBottom,
								anchorHeight: anchorHeight,
								left: anchorLeft,
								right: windowWidth - anchorRight,
								anchorWidth: anchorWidth,
								positionAtTopOfTarget: addAtTop,
								positionAtLeftOfTarget: addAtLeft,
								maxWidth: addAtLeft ? anchorRight : windowWidth - anchorLeft,
								maxHeight: addAtTop
									? anchorTop - 16
									: windowHeight - anchorBottom - 16,
								baseWidth: this.widthSizeMap[this.widthSize],
								minWidth: this.widthSizeMap[this.widthSize],
							};
						})
					);
				})
			);
		})
	);

	public open(): void {
		this.visible$$.next(true);
		this.opened$$.next();
	}

	public close(): void {
		this.visible$$.next(false);
		this.closed$$.next();
	}

	public toggle(): void {
		if (this.visible$$.value) {
			this.close();
		} else {
			this.open();
		}
	}

	@HostListener('window:resize')
	@HostListener('window:orientationchange')
	public _onResize(): void {
		cancelAnimationFrame(this._animationFrameId!);

		this._animationFrameId = window.requestAnimationFrame(() => {
			this.resized$$.next();
		});
	}
}
